import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const PENDING_MERCHANT_SETUP_KEY = "splitkart_pending_merchant_setup";

export type PendingMerchantSetup = {
  platformType: string;
  storeDomain: string;
  storeName: string;
  accessToken?: string | null;
  widgetInstalled?: boolean;
  billingProvider?: string;
  signupSource?: string;
};

const normalizePendingSetup = (value: unknown): PendingMerchantSetup | null => {
  if (!value || typeof value !== "object") return null;

  const setup = value as Record<string, unknown>;
  const platformType = String(setup.platformType || setup.platform_type || "").trim();
  const storeDomain = String(setup.storeDomain || setup.store_domain || "").trim();
  const storeName = String(setup.storeName || setup.store_name || "").trim();

  if (!platformType || !storeDomain || !storeName) return null;

  return {
    platformType,
    storeDomain,
    storeName,
    accessToken: typeof setup.accessToken === "string" ? setup.accessToken : null,
    widgetInstalled: Boolean(setup.widgetInstalled || setup.widget_installed),
    billingProvider: String(
      setup.billingProvider ||
        setup.billing_provider ||
        (platformType === "shopify" ? "shopify" : "razorpay"),
    ),
    signupSource: String(
      setup.signupSource ||
        setup.signup_source ||
        (platformType === "shopify" ? "shopify_app_store" : "direct_website"),
    ),
  };
};

export const savePendingMerchantSetup = (setup: PendingMerchantSetup) => {
  localStorage.setItem(PENDING_MERCHANT_SETUP_KEY, JSON.stringify(setup));
};

export const clearPendingMerchantSetup = () => {
  localStorage.removeItem(PENDING_MERCHANT_SETUP_KEY);
};

export const getPendingMerchantSetup = (user?: User | null): PendingMerchantSetup | null => {
  const stored = localStorage.getItem(PENDING_MERCHANT_SETUP_KEY);
  if (stored) {
    try {
      const setup = normalizePendingSetup(JSON.parse(stored));
      if (setup) return setup;
    } catch {
      localStorage.removeItem(PENDING_MERCHANT_SETUP_KEY);
    }
  }

  return normalizePendingSetup(user?.user_metadata?.merchant_setup);
};

export const completePendingMerchantSetup = async (user: User | null | undefined) => {
  if (!user) return null;

  const setup = getPendingMerchantSetup(user);
  if (!setup) return null;

  const { data: existingLinks, error: linkError } = await supabase
    .from("merchant_users")
    .select("merchant_id")
    .eq("user_id", user.id)
    .limit(1);

  if (linkError) throw linkError;

  if (existingLinks && existingLinks.length > 0) {
    clearPendingMerchantSetup();
    return existingLinks[0].merchant_id;
  }

  const { data: merchantId, error } = await supabase.rpc("create_merchant_with_user", {
    p_user_id: user.id,
    p_store_domain: setup.storeDomain,
    p_store_name: setup.storeName,
    p_platform_type: setup.platformType,
    p_access_token: setup.accessToken || null,
    p_widget_installed: Boolean(setup.widgetInstalled),
    p_billing_provider: setup.billingProvider || (setup.platformType === "shopify" ? "shopify" : "razorpay"),
    p_signup_source: setup.signupSource || (setup.platformType === "shopify" ? "shopify_app_store" : "direct_website"),
  } as any);

  if (error) throw error;

  clearPendingMerchantSetup();
  return merchantId as string;
};
